import { InputHTMLAttributes, forwardRef, useState } from 'react'
import { clsx } from 'clsx'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../core/hooks/useTranslation'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string
  error?: string
  helperText?: string
  value: string
  onChange: (value: string) => void
  showPasswordToggle?: boolean
  required?: boolean
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ 
    className,
    label,
    error,
    helperText,
    value,
    onChange,
    showPasswordToggle = true,
    required,
    disabled,
    id,
    ...props
  }, ref) => {
    const { isRTL } = useTranslation()
    const [showPassword, setShowPassword] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const inputId = id || `password-input-${Math.random().toString(36).substr(2, 9)}`
    
    const inputType = showPassword ? 'text' : 'password'
    const hasErrorIcon = error && !showPasswordToggle

    return (
      <div className="space-y-2">
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm text-gray-700 mb-1"
          >
            {label}
            {required && <span className="text-danger-500 mr-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {/* Lock Icon */}
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none z-10`}>
            <span className="text-gray-400">
              <LockClosedIcon className="w-5 h-5" />
            </span>
          </div>
          
          {/* Input */}
          <input
            id={inputId}
            type={inputType}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            className={clsx(
              'input',
              error && 'input-error',
              isRTL ? 'pr-10' : 'pl-10',
              (showPasswordToggle || hasErrorIcon) && (isRTL ? 'pl-10' : 'pr-10'),
              className
            )}
            ref={ref}
            {...props}
          />
          
          {/* Password Toggle or Error Icon */}
          {showPasswordToggle && !error && (
            <button
              type="button"
              className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-gray-400 hover:text-gray-600 transition-colors`}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          )}
          
          {error && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none`}>
              <AlertCircle className="w-5 h-5 text-danger-500" />
            </div>
          )}
        </div>
        
        {error && (
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <AlertCircle className="w-4 h-4 text-danger-500 flex-shrink-0" />
            <p className="text-sm text-danger-600 font-medium">{error}</p>
          </div>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-secondary-500">{helperText}</p>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'

export default PasswordInput

